import { useState } from 'react'
import { ChevronDown, LogOut, User, Building2, Sun, Moon, RefreshCw, Palette, GitBranch, Star, Menu, Lock } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { useTabsStore } from '@/stores/tabs.store'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog } from '@/components/ui/Dialog'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { DonemSwitcher } from '@/layouts/DonemSwitcher'
import { BildirimMenu } from '@/layouts/BildirimMenu'
import { AbonelikDurumRozet } from '@/components/ui/AbonelikDurumRozet'

type PendingSwitch = { tip: 'firma'; id: string; ad: string } | { tip: 'sube'; id: string; ad: string }

// Masaüstünde firma/şube değişince açık sekmeler eski firmaya ait olduğundan sıfırlanır.
const isTauri = typeof window !== 'undefined' && !!(window as { __TAURI__?: unknown }).__TAURI__

export function Header() {
  const { kullanici, firma, sube, logout, switchFirm, switchSube, availableFirmalar, availableSubeler, isLoading } = useAuthStore()
  const { tema, toggleTema } = useUIStore()
  const [gorünümAcik, setGörünümAcik] = useState(false)
  const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const handleSwitchFirm = (firmaId: string, firmaAd: string) => {
    if (firmaId === firma?.id) return
    setPendingSwitch({ tip: 'firma', id: firmaId, ad: firmaAd })
  }

  const handleSwitchSube = (subeId: string, subeAd: string) => {
    if (subeId === sube?.id) return
    setPendingSwitch({ tip: 'sube', id: subeId, ad: subeAd })
  }

  const handleConfirmSwitch = async () => {
    if (!pendingSwitch) return
    const snap = pendingSwitch
    setPendingSwitch(null)
    if (snap.tip === 'firma') {
      await switchFirm(snap.id)
    } else {
      await switchSube(snap.id)
    }
    queryClient.clear()
    if (isTauri) useTabsStore.getState().reset('/dashboard', 'Ana Sayfa')
    else navigate('/dashboard')
  }

  const showFirmaSwitcher = availableFirmalar.length > 1
  const showSubeSwitcher = availableSubeler.length > 1

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-header bg-bg-surface border-b border-bg-border z-40 flex items-center px-3 lg:px-4 gap-2 lg:gap-4 print:hidden">
        {/* Hamburger — sadece mobilde (< md), tablet'te sidebar görünür */}
        <button
          onClick={() => useUIStore.getState().toggleMobileSidebar()}
          className="md:hidden w-8 h-8 flex items-center justify-center rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2 lg:w-sidebar shrink-0">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <span className="text-white text-xs font-bold">N</span>
          </div>
          <span className="font-semibold text-text-primary text-md hidden sm:inline">iNova Muhasebe</span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Sağ taraf */}
        <div className="flex items-center gap-2">
          {/* Şube seçici */}
          {showSubeSwitcher && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className={cn(
                  'hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-md',
                  'bg-bg-elevated border border-bg-border text-sm text-text-secondary',
                  'hover:text-text-primary transition-colors',
                  isLoading && 'opacity-60 pointer-events-none'
                )}>
                  <GitBranch className="w-3.5 h-3.5" />
                  <span>{sube?.ad ?? 'Şube'}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={4}
                  className="min-w-48 bg-bg-surface border border-bg-border rounded-lg shadow-dropdown p-1 z-50 animate-slide-in-up"
                >
                  <div className="px-3 py-2 border-b border-bg-border mb-1">
                    <p className="text-xs text-text-muted">Şube Değiştir</p>
                  </div>
                  {availableSubeler.map((s: any) => (
                    <DropdownMenu.Item
                      key={s.id}
                      onSelect={() => handleSwitchSube(s.id, s.ad)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer outline-none',
                        s.id === sube?.id
                          ? 'text-primary bg-primary/10'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                      )}
                    >
                      {s.merkez && <Star className="w-3 h-3 text-warning shrink-0" />}
                      {!s.merkez && <div className="w-3 h-3 shrink-0" />}
                      <span className="flex-1">{s.ad}</span>
                      {s.id === sube?.id && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}

          {/* Abonelik durum rozeti (yaklaşıyor / ek süre / salt-okunur) */}
          <AbonelikDurumRozet />

          {/* Salt-okunur rozeti — firma adının SOLUNDA (pasif/askıya alınmış firma) */}
          {firma?.aktif === false && (
            <span
              title={firma.platformKilitli
                ? 'Firma platform tarafından askıya alındı — yalnızca görüntüleme. Yeniden aktifleştirmek için yönetici (destek) ile iletişime geçin.'
                : 'Firma dondurulmuş — yalnızca görüntüleme. Firmalarım ekranından yeniden açabilirsiniz.'}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-error/10 border border-error/30 text-xs font-medium text-error whitespace-nowrap"
            >
              <Lock className="w-3 h-3" />
              <span className="hidden sm:inline">{firma.platformKilitli ? 'Askıya alındı · salt-okunur' : 'Salt-okunur'}</span>
              <span className="sm:hidden">Salt-okunur</span>
            </span>
          )}

          {/* Firma / Şube seçici */}
          {showFirmaSwitcher ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md',
                  'bg-bg-elevated border border-bg-border text-sm text-text-secondary',
                  'hover:text-text-primary transition-colors',
                  isLoading && 'opacity-60 pointer-events-none'
                )}>
                  <Building2 className="w-3.5 h-3.5" />
                  <span>{firma?.ad ?? sube?.ad ?? 'Firma'}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={4}
                  className={cn(
                    'min-w-52 bg-bg-surface border border-bg-border rounded-lg shadow-dropdown',
                    'p-1 z-50 animate-slide-in-up'
                  )}
                >
                  <div className="px-3 py-2 border-b border-bg-border mb-1">
                    <p className="text-xs text-text-muted">Firma Değiştir</p>
                  </div>
                  {availableFirmalar.map((f) => (
                    <DropdownMenu.Item
                      key={f.id}
                      onSelect={() => handleSwitchFirm(f.id, f.ad)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer outline-none',
                        f.id === firma?.id
                          ? 'text-primary bg-primary/10'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                      )}
                    >
                      {f.id === firma?.id && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      )}
                      {f.id !== firma?.id && (
                        <div className="w-1.5 h-1.5 shrink-0" />
                      )}
                      <span className="flex-1">{f.ad}</span>
                      {isLoading && f.id !== firma?.id && (
                        <RefreshCw className="w-3 h-3 animate-spin text-text-muted" />
                      )}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : (
            <button className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-bg-elevated border border-bg-border text-sm text-text-secondary hover:text-text-primary transition-colors">
              <Building2 className="w-3.5 h-3.5" />
              <span>{firma?.ad ?? sube?.ad ?? 'Ana Merkez'}</span>
            </button>
          )}

          {/* Çalışma Dönemi seçici (firma seçiminin yanında) */}
          <DonemSwitcher />

          {/* Tema değiştir */}
          <button
            onClick={toggleTema}
            title={tema === 'dark' ? 'Açık Temaya Geç' : 'Koyu Temaya Geç'}
            className="w-8 h-8 flex items-center justify-center rounded-md text-text-muted hover:text-text-primary hover:bg-bg-elevated transition-colors"
          >
            {tema === 'dark'
              ? <Sun className="w-4 h-4" />
              : <Moon className="w-4 h-4" />
            }
          </button>

          {/* Bildirimler */}
          <BildirimMenu />

          {/* Kullanıcı menüsü */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className={cn(
                'flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md',
                'text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
                'transition-colors border border-transparent hover:border-bg-border'
              )}>
                <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="font-medium hidden sm:inline">{kullanici?.ad} {kullanici?.soyad}</span>
                <ChevronDown className="w-3 h-3 hidden sm:block" />
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={4}
                className={cn(
                  'min-w-48 bg-bg-surface border border-bg-border rounded-lg shadow-dropdown',
                  'p-1 z-50 animate-slide-in-up'
                )}
              >
                <div className="px-3 py-2 border-b border-bg-border mb-1">
                  <p className="text-sm font-medium text-text-primary">
                    {kullanici?.ad} {kullanici?.soyad}
                  </p>
                  <p className="text-xs text-text-muted">{firma?.ad}</p>
                </div>

                <DropdownMenu.Item
                  onSelect={() => { if (isTauri) useTabsStore.getState().openTab('/ayarlar/profil', 'Profil'); else navigate('/ayarlar/profil') }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-md cursor-pointer outline-none"
                >
                  <User className="w-4 h-4" />
                  Profil
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  onSelect={() => setGörünümAcik(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-md cursor-pointer outline-none"
                >
                  <Palette className="w-4 h-4" />
                  Görünüm Tercihi
                </DropdownMenu.Item>

                <DropdownMenu.Separator className="my-1 border-bg-border" />

                <DropdownMenu.Item
                  onSelect={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/10 rounded-md cursor-pointer outline-none"
                >
                  <LogOut className="w-4 h-4" />
                  Çıkış Yap
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </header>

      {/* Firma / Şube Değişim Onay Dialogu */}
      <Dialog
        open={pendingSwitch !== null}
        onClose={() => setPendingSwitch(null)}
        title={pendingSwitch?.tip === 'firma' ? 'Firma Değiştir' : 'Şube Değiştir'}
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            {pendingSwitch?.tip === 'firma'
              ? <><span className="font-semibold text-text-primary">{pendingSwitch.ad}</span> firmasına geçmek istediğinize emin misiniz?</>
              : <><span className="font-semibold text-text-primary">{pendingSwitch?.ad}</span> şubesine geçmek istediğinize emin misiniz?</>
            }
          </p>
          <p className="text-xs text-text-muted">
            Değişiklik sonrası ana sayfaya yönlendirileceksiniz.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => setPendingSwitch(null)}
              className="px-4 py-2 text-sm rounded-md border border-bg-border text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleConfirmSwitch}
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {isLoading ? 'Geçiliyor…' : 'Evet, Geç'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Görünüm Tercihi Modal */}
      <Dialog open={gorünümAcik} onClose={() => setGörünümAcik(false)} title="Görünüm Tercihi">
        <ThemeSwitcher />
      </Dialog>
    </>
  )
}
